import { SectionHeading, SectionLabel } from "./Section";

const features = [
    {
        title: "AI Decision Cards",
        body: "Every chat response ships with executable CTAs — approve, reject, assign, simulate, follow up, or escalate.",
        chip: "Executable",
        preview: (
            <div className="rounded-2xl border border-black/5 bg-white p-3.5">
                <div className="text-[10px] font-semibold tracking-[0.22em] uppercase text-violet-600">
                    Decision
                </div>
                <div className="mt-1 text-[13px] font-medium text-black">
                    Approve hiring plan Q3
                </div>
                <div className="mt-3 flex gap-1.5">
                    <span className="inline-flex items-center gap-1 rounded-full bg-black text-white text-[10px] font-medium px-2.5 py-1">
                        Approve
                    </span>
                    <span className="inline-flex items-center gap-1 rounded-full bg-neutral-100 text-neutral-700 text-[10px] font-medium px-2.5 py-1">
                        Simulate
                    </span>
                    <span className="inline-flex items-center gap-1 rounded-full bg-neutral-100 text-neutral-700 text-[10px] font-medium px-2.5 py-1">
                        Assign
                    </span>
                </div>
            </div>
        ),
    },
    {
        title: "CEO Money Meter",
        body: "See current money health and the future money impact — before every single decision.",
        chip: "Forecast",
        preview: (
            <div className="rounded-2xl border border-black/5 bg-white p-3.5 flex items-center gap-3">
                <MiniMeter />
                <div>
                    <div className="text-[10px] font-semibold tracking-[0.22em] uppercase text-violet-600">
                        Money Health
                    </div>
                    <div className="mt-0.5 font-display text-xl font-medium text-black leading-none">
                        72 → 81
                    </div>
                    <div className="text-[11px] text-emerald-600 mt-1">
                        +9 after decision
                    </div>
                </div>
            </div>
        ),
    },
    {
        title: "Business Scan AI",
        body: "Scan invoices, reports, dashboards, whiteboards, and operational data — instantly become insights and action cards.",
        chip: "Scan · Convert",
        preview: (
            <div className="relative rounded-2xl border border-black/5 bg-white p-3.5 overflow-hidden">
                <div className="text-[10px] font-semibold tracking-[0.22em] uppercase text-violet-600">
                    Scan
                </div>
                <div className="mt-1 text-[13px] font-medium text-black">
                    invoice_dec.pdf
                </div>
                <div className="mt-2 grid grid-cols-3 gap-1.5">
                    {["Insight", "Risk", "Task"].map((t) => (
                        <span key={t} className="rounded-lg bg-neutral-50 border border-black/5 text-[10px] py-1 text-center text-neutral-600">
                            {t}
                        </span>
                    ))}
                </div>
                <div className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-violet-500 to-transparent anim-scan" />
            </div>
        ),
    },
];

function MiniMeter() {
    const r = 20;
    const c = 2 * Math.PI * r;
    const pct = 0.81;
    return (
        <svg width="52" height="52" viewBox="0 0 52 52">
            <circle cx="26" cy="26" r={r} stroke="#F3F4F6" strokeWidth="6" fill="none" />
            <circle
                cx="26"
                cy="26"
                r={r}
                stroke="#8B5CF6"
                strokeWidth="6"
                fill="none"
                strokeDasharray={c}
                strokeDashoffset={c * (1 - pct)}
                strokeLinecap="round"
                transform="rotate(-90 26 26)"
            />
        </svg>
    );
}

export default function Innovation() {
    return (
        <section id="innovation" data-testid="innovation-section" className="relative py-28 sm:py-36">
            <div className="mx-auto max-w-7xl px-6 lg:px-8">
                <div className="reveal max-w-3xl">
                    <SectionLabel num="03">Core Innovation</SectionLabel>
                    <SectionHeading className="mt-6">
                        Not answers.
                        <br />
                        <span className="italic font-normal">
                            Executable business decisions.
                        </span>
                    </SectionHeading>
                </div>

                <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-5">
                    {features.map((f, i) => (
                        <div
                            key={f.title}
                            data-testid={`feature-${f.title.toLowerCase().replace(/\s+/g, "-")}`}
                            className="reveal glass rounded-3xl p-8 hover:-translate-y-1 hover:shadow-[0_20px_60px_-20px_rgba(139,92,246,0.25)] transition-all duration-500 flex flex-col"
                            style={{ transitionDelay: `${i * 100}ms` }}
                        >
                            <div className="flex items-center justify-between">
                                <span className="inline-flex items-center gap-1.5 text-[10px] font-semibold tracking-[0.2em] uppercase text-violet-600 bg-violet-50 rounded-full px-2.5 py-1">
                                    {f.chip}
                                </span>
                                <span className="font-mono-alt text-[11px] text-neutral-400">
                                    0{i + 1}
                                </span>
                            </div>
                            <div className="mt-8 font-display text-2xl sm:text-[28px] font-medium tracking-tight text-black">
                                {f.title}
                            </div>
                            <p className="mt-3 text-[14px] sm:text-[15px] text-neutral-600 leading-relaxed">
                                {f.body}
                            </p>
                            <div className="mt-6">{f.preview}</div>
                        </div>
                    ))}
                </div>
            </div>
        </section>
    );
}
